/**
 * Client-side error reporter for CareOS. POSTs uncaught crashes to
 * /api/log-error, which forwards to the careos-api `app-error-log`
 * action and writes a row in careos_app_errors.
 *
 * Safety: wrapped in try/catch so the reporter itself can never
 * throw; drops events when offline; uses navigator.sendBeacon where
 * available so reports survive page unload.
 */

export interface ReportableError {
  message: string;
  stack?: string | null | undefined;
  errorType?: string | null | undefined;
  source?: "client" | "server" | undefined;
  url?: string | null | undefined;
  route?: string | null | undefined;
  context?: Record<string, unknown> | null | undefined;
}

const ENDPOINT = "/api/log-error";

const recentlySeen = new Set<string>();
const RECENT_LIMIT = 50;

/**
 * Errors we never want logged. These are not actionable from app code:
 * they originate in framework / browser / extension layers and only
 * pollute the dashboard.
 *
 * `$RS` — Next.js streaming-Suspense helper emitted inline in the HTML.
 * Does `document.getElementById(a).parentNode.removeChild(a)`; throws
 * if a browser extension (Grammarly, Dark Reader, ad blockers, Google
 * Translate) or fast-nav has already stripped the placeholder
 * <template>. Harmless — the streamed chunk either landed or the user
 * moved on.
 */
function isIgnorable(payload: ReportableError): boolean {
  const stack = payload.stack ?? "";
  const msg = payload.message ?? "";
  if (stack.includes("$RS") && msg.includes("parentNode")) return true;
  // ResizeObserver loop noise — Chrome fires this on layout thrash;
  // not actionable, never user-visible.
  if (msg.includes("ResizeObserver loop")) return true;
  return false;
}

function dedupKey(payload: ReportableError): string {
  return [
    payload.source ?? "client",
    payload.errorType ?? "",
    (payload.stack ?? payload.message ?? "").split("\n", 1)[0],
    payload.route ?? "",
  ].join("|");
}

export function reportClientError(
  input: unknown,
  extras: Partial<ReportableError> = {},
): void {
  try {
    if (typeof window === "undefined") return;
    if (navigator && navigator.onLine === false) return;

    const payload = normalize(input, extras);
    if (!payload) return;
    if (isIgnorable(payload)) return;

    const key = dedupKey(payload);
    if (recentlySeen.has(key)) return;
    recentlySeen.add(key);
    if (recentlySeen.size > RECENT_LIMIT) {
      const first = recentlySeen.values().next().value as string | undefined;
      if (first !== undefined) recentlySeen.delete(first);
    }

    const body = JSON.stringify({
      source: payload.source ?? "client",
      error_type: payload.errorType ?? null,
      message: payload.message,
      stack: payload.stack ?? null,
      url: payload.url ?? window.location.href,
      route: payload.route ?? routeFromPath(window.location.pathname),
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      context: payload.context ?? null,
    });

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      try {
        const blob = new Blob([body], { type: "application/json" });
        const queued = navigator.sendBeacon(ENDPOINT, blob);
        if (queued) return;
      } catch {
        /* fall through */
      }
    }

    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* never throw from the reporter */
  }
}

function normalize(
  input: unknown,
  extras: Partial<ReportableError>,
): ReportableError | null {
  if (input && typeof input === "object" && "message" in (input as object)) {
    const err = input as Error;
    return {
      message: String(err.message || extras.message || "(no message)"),
      stack: err.stack ?? extras.stack ?? null,
      errorType: err.name || extras.errorType || "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  if (typeof input === "string") {
    return {
      message: input || extras.message || "(no message)",
      stack: extras.stack ?? null,
      errorType: extras.errorType ?? "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  if (extras.message) {
    return {
      message: extras.message,
      stack: extras.stack ?? null,
      errorType: extras.errorType ?? "Error",
      source: extras.source ?? "client",
      url: extras.url,
      route: extras.route,
      context: extras.context,
    };
  }
  return null;
}

function routeFromPath(pathname: string): string {
  return pathname
    .split("/")
    .map((seg) => {
      if (!seg) return seg;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-/.test(seg)) return "[id]";
      if (/^\d+$/.test(seg)) return "[id]";
      if (/^[0-9a-f]{16,}$/i.test(seg)) return "[id]";
      if (seg.includes("@")) return "[email]";
      return seg;
    })
    .join("/");
}
